import { format, formatDistanceToNowStrict, addHours, addMinutes, add, sub } from 'date-fns';
import { enUS } from 'date-fns/locale/en-US';
import { getTimezoneOffset, formatInTimeZone } from 'date-fns-tz';
import { ClockFormat } from '@lio/rule-engine';

export const ShortDateFormat = 'MM/dd/yyyy';
export const LongDateTimeFormat = 'MM/dd/yyyy HH:mm:ss zzz'; // 24 hr format
export const LongTwelveHourDateTimeFormat = 'MM/dd/yyyy h:mm aa zzz'; // 12 hr format
export const DateTimeFormat = 'MM/dd/yyyy HH:mm:ss'; // 24 hr format
export const TwelveHourDateFormat = 'MM/dd/yyyy h:mm aa'; // 12 hr format
export const LongDateFormatWithoutSeconds = 'MM/dd/yyyy HH:mm zzz'; // 24 hr format
export const TwelveHourDateFormatWithoutSeconds = 'MM/dd/yyyy h:mm aa'; // 12 hr format

export function getDateFormatter(clockFormat: ClockFormat, dateFormat: string) {
  switch (dateFormat) {
    case LongDateTimeFormat:
      return clockFormat === ClockFormat.TwelveHour ? LongTwelveHourDateTimeFormat : LongDateTimeFormat;
    case DateTimeFormat:
      return clockFormat === ClockFormat.TwelveHour ? TwelveHourDateFormat : DateTimeFormat;
    case LongDateFormatWithoutSeconds:
      return clockFormat === ClockFormat.TwelveHour ? LongTwelveHourDateTimeFormat : LongDateFormatWithoutSeconds;
    case ShortDateFormat:
      return ShortDateFormat;
    default:
      return LongDateTimeFormat;
  }
}
export function getFormattedDistance(date: Date): string {
  const options = {
    locale: {
      ...enUS,
      formatDistance: (unit: string) => {
        switch (true) {
          case unit === 'xSeconds':
            return format(date, 'hh:mm aa');
          case unit === 'xMinutes':
            return format(date, 'hh:mm aa');
          case unit === 'xHours':
            return format(date, 'hh:mm aa');
          default:
            return format(date, 'MM/dd/yy hh:mm aa');
        }
      },
    },
  };
  return formatDistanceToNowStrict(date, options);
}

// Browser/User Timezone does NOT use DST (Argentina/Arizona, etc)
export function localTZDoesNotShiftForDST() {
  const localtoday = new Date().getTimezoneOffset();
  const local6MonthsAhead = add(new Date(), { months: 6 }).getTimezoneOffset();
  const local6MonthsPrevious = sub(new Date(), { months: 6 }).getTimezoneOffset();
  return localtoday === local6MonthsAhead && localtoday === local6MonthsPrevious;
}

// New York is currently in DST
function nyInDST(date: Date): boolean {
  const tzString = 'America/New_York';
  const utcOffsetEstimateMinutes = Math.abs(getTimezoneOffset(tzString, date) / 60000);
  return utcOffsetEstimateMinutes === 300;
}

// SET DATES TO MIDNIGHT BEFORE HITTING THE SERVER
// input should be a faked local midnight date, but may be TZ midnight already
function setDateToMidnight(date: Date, tzString: string = 'America/New_York'): Date {
  const inputDate = setInputDate(date, tzString);
  const utcOffsetEstimateMinutes = Math.abs(getTimezoneOffset(tzString, inputDate) / 60000); // <- here
  const utcOffsetEstimateHours = Math.floor(utcOffsetEstimateMinutes / 60);
  const utcOffsetEstimateRemainderMinutes = utcOffsetEstimateMinutes - utcOffsetEstimateHours * 60;

  const dateHoursOff = formatInTimeZone(inputDate, tzString, 'HH');

  // shortcut if already midnight or close to midnight but off by DST/ST only)
  if (dateHoursOff === '00' || dateHoursOff === '01' || dateHoursOff === '23') {
    return adjustBasedOnHoursToGetToMidnight(dateHoursOff, inputDate);
  }

  // Because of DST and bugs in date-fns, inputDate.getDate() might get confused at tz midnight (and return the day before)
  // Since only the day can get messed up given where DST transitions happened (never beginning or end of a month)
  // Get the day from the shifted TZ Date
  const day = getDayFromShiftedTZFormattedDate(inputDate, tzString);
  let midnightET = new Date(
    Date.UTC(
      inputDate.getFullYear(),
      inputDate.getMonth(),
      day,
      utcOffsetEstimateHours,
      utcOffsetEstimateRemainderMinutes,
      0,
    ),
  );

  // Adjust time by 1 hour if local time is not in DST but New York is
  if (localTZDoesNotShiftForDST() && nyInDST(midnightET)) {
    midnightET = add(midnightET, { hours: 1 });
  }

  const hours = formatInTimeZone(midnightET, tzString, 'HH');
  return adjustBasedOnHoursToGetToMidnight(hours, midnightET);
}

function setInputDate(date: Date, originTz: string): Date {
  const localTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  // if local timezone offset is postive from date-fns, we will need to get the date info from the local date time
  // so that it is in the right day in that originTz
  const litmusTimeZoneOffset = getTimezoneOffset(localTz, date);

  if (litmusTimeZoneOffset > 0) {
    // will need to seed the dates with a UTC version of this date so we get the right date, otherwise, the tz conversion
    // could start with the previous day

    // this will only work as intended for origin tz offsets that are behind UTC, including all states in the U.S.
    const originTzOffsetMinutes = Math.abs(getTimezoneOffset(originTz, date) / 60000);
    // on some times date-fns will be off by one hour. In the worst case this will make the date created one hour
    // before the intended date. As the date is more important, shift this offset by 1
    const saferOriginTzOffsetHour = Math.floor(originTzOffsetMinutes / 60) + 1;

    return new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), saferOriginTzOffsetHour, 0, 0));
  }

  return date;
}

function getDayFromShiftedTZFormattedDate(date: Date, tzString: string): number {
  const shiftedDate = addHours(date, 1);
  const tzDay = formatInTimeZone(shiftedDate, tzString, 'dd');
  return +tzDay;
}

// due to bugs in date-fns-tz and possible shifts accross DST/ST boundaries
// adjust based on DST/ST by using the formatted local time, which is correct where the
// offset might have been off by one hour in certain windows of time due to a bug
function adjustBasedOnHoursToGetToMidnight(hours: string, date: Date): Date {
  // this should only be used for midnight or one hour from midnight times
  if (hours !== '00') {
    let hoursToSubtract = +hours;
    if (hoursToSubtract === 23) {
      hoursToSubtract = -1;
    }
    const correctedDate = addHours(date, -hoursToSubtract);
    return correctedDate;
  }

  return date;
}

function fakeLocalMidnightFromTZMidnight(date: Date, tzString: string = 'America/New_York'): Date {
  // Should get date from TZ midnight
  // but MAY be getting data from its last output if user continually clicks without saving
  // and perhaps other UI phenomena
  if (typeof date === 'string') {
    date = new Date(date);
  }
  const inputDate = setInputDate(date, tzString);

  // Difference in minutes from UTC to this time (so negative of normal offset from date-fns)
  // note that this does not have the same DST bug as the date-fns offset
  const utcOffsetLocalMinutesEstimate = inputDate.getTimezoneOffset();

  const utcOffsetLocalHoursEstimate = Math.floor(utcOffsetLocalMinutesEstimate / 60);
  const dateHours = inputDate.getUTCHours();
  // if the hours are already what we expected, then do not convert
  if (dateHours === getHoursFromOffset(utcOffsetLocalHoursEstimate)) {
    return inputDate;
  }

  // sanitize the date to TZ Midnight so the starting point is always the same, as we
  // are not building a date from scratch from here, only shifting it
  const sanitizedDate = setDateToMidnight(inputDate, tzString);

  const utcOffsetOrigTZMinutes = getTimezoneOffset(tzString, sanitizedDate) / 60000;
  const utcMidnight = addMinutes(sanitizedDate, utcOffsetOrigTZMinutes);
  const localMidnightEstimate = addMinutes(utcMidnight, utcOffsetLocalMinutesEstimate);

  const localTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const hoursOff = formatInTimeZone(localMidnightEstimate, localTz, 'HH');

  return adjustBasedOnHoursToGetToMidnight(hoursOff, localMidnightEstimate);
}

// offset may be negative, if so, it will be down from 24 by that amount
function getHoursFromOffset(hoursOffset: number): number {
  // return the day hours given the offset
  if (hoursOffset >= 0) {
    return hoursOffset;
  }
  return 24 + hoursOffset;
}

function formatDateToTimezone(
  // USE THIS FUNCTION TO DISPLAY DATES IN THE UI
  date: Date | string | undefined,
  format = 'MM/dd/yyyy',
  tzString: string = 'America/New_York',
): string {
  if (!date) {
    return '';
  }
  return formatInTimeZone(date, tzString, format);
}

const isDateValid = (date: Date | null): boolean => {
  return date instanceof Date && !Number.isNaN(date.getTime());
};

const convertToISOStringIfDate = (date: Date | string): string => {
  if (date instanceof Date) {
    return date.toISOString();
  }

  return date;
};

function getDateRange({ fromDate, toDate }: { fromDate?: Date; toDate?: Date }): string {
  return `${fromDate ? format(fromDate, ShortDateFormat) : ''} - ${toDate ? format(toDate, ShortDateFormat) : ''}`;
}

// fakeLocalMidnightFromTZMidnight is used to transform server EST midnight dates to local midnight dates
// it sits just before the date is displayed in the UI
// setDatetoMidngiht is used to transform local midnight dates to server EST midnight dates...
// it sits just before the date is sent to the server
export {
  convertToISOStringIfDate,
  isDateValid,
  setDateToMidnight,
  formatDateToTimezone,
  fakeLocalMidnightFromTZMidnight,
  getDateRange,
};

export function getUtcDateAtMidnightEST_DSTSafe(date: Date): Date {
  const timeZoneOffset = getTimezoneOffset('America/New_York', new Date(date)); // get the timezone Offset for EST (which handles DST)
  const hoursFromMidnightUTC = Math.abs(timeZoneOffset / (1000 * 60 * 60)); // convert to positive hours
  const tempDate = new Date(date);
  // TODO: this will be incorrect in non-UTC timezones when passed time is after UTC midnight, such as in the evening in Seattle time or after 8/9pm in New York time
  // it ends up with a date that is one day after of the intended date (as the UTC date is already the next day)
  // when running in UTC, this will not have issues
  tempDate.setUTCHours(hoursFromMidnightUTC, 0, 0, 0);
  return tempDate;
}
